import { useContext, useEffect, useState } from "react"
import { APIError } from "../../../app/appTypes"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { impossible } from "../../../app/Utils"
import { parseISODate } from "../../../app/Utils"
import { showError } from "../../notifications/notificationsSlice"
import { DocumentSectionHeader } from "../components/DocumentSectionHeader"
import { SectionDeleteContext } from "../DocumentContent"
import { updateDocSection } from "../documentsApi"
import { isDocEditable, sectionSelectors, selectDocument } from "../documentSlice"
import { DocumentSectionDeleteDialog } from "../utils/DeleteSectionDialog"
import { defSimplePersonData, SimplePersonalDataPicker, SimplePersonData } from "../PersonalDataPicker"
import {
    createRiskAnalysisMitigationSchedule,
    deleteRiskAnalysisMitigationSchedule,
    generateRiskAnalysisMitigationSchedules,
    loadRiskAnalysisMitigationSchedules,
    RiskAnalysisMitigationSchedule,
    updateRiskAnalysisMitigationSchedule,
} from "./riskAnalysisApi"
import {
    Box,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material"
import { LoadingButton } from "@mui/lab"
import { DatePicker } from "@mui/x-date-pickers"
import { format, parseISO } from "date-fns"
import { Localized } from "@fluent/react"
import { JustText, useJustText } from "../../../app/RichTextEditor"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import { SaveButton } from "../../../app/components/SaveButton"

export const MitigationScheduleSection = (props: { sectionId: string }) => {
    const { sectionId } = props
    const dispatch = useAppDispatch()
    const section = useAppSelector(state => sectionSelectors.selectById(state, sectionId)) ?? impossible()
    const { header } = useAppSelector(selectDocument)?.doc ?? impossible()
    const { numeration } = section
    const isEditable = useAppSelector(isDocEditable)
    const deleteSection = useContext(SectionDeleteContext)

    // Section header state
    const [error, setError] = useState<APIError | null>(null)
    const [del, setDel] = useState(false)
    const [deleting, setDeleting] = useState(false)

    // Items state
    const [items, setItems] = useState<RiskAnalysisMitigationSchedule[]>([])
    const [loading, setLoading] = useState(false)

    // Add/edit dialog — null=closed, 'new'=create, object=edit
    const [editItem, setEditItem] = useState<RiskAnalysisMitigationSchedule | 'new' | null>(null)
    const [saving, setSaving] = useState(false)
    const titleEditor = useJustText({ defaultValue: '', onChange: () => {} })
    const [formDescription, setFormDescription] = useState('')
    const [formResponsible, setFormResponsible] = useState<SimplePersonData>(defSimplePersonData)
    const [formDueDate, setFormDueDate] = useState<Date | null>(null)

    // Delete item state
    const [deleteItem, setDeleteItem] = useState<RiskAnalysisMitigationSchedule | null>(null)
    const [deletingItem, setDeletingItem] = useState(false)

    const [generating, setGenerating] = useState(false)

    const [regenerateOpen, setRegenerateOpen] = useState(false)
    const [clearExisting, setClearExisting] = useState(false)

    useEffect(() => {
        const load = async () => {
            setLoading(true)
            try {
                const result = await dispatch(loadRiskAnalysisMitigationSchedules({ docId: Number(header.id) })).unwrap()
                setItems(result)
            } catch {
                dispatch(showError('error'))
            } finally {
                setLoading(false)
            }
        }
        load()
    }, [header.id])

    const handleApprove = async (newName: string) => {
        if (newName) {
            try {
                await dispatch(updateDocSection({ ...section, name: newName })).unwrap()
            } catch (error) {
                setError(error as APIError)
            }
        }
    }

    const handleDeleteClick = () => setDel(true)
    const handleConfirmDeleteClick = async () => {
        try {
            setDeleting(true)
            deleteSection(sectionId)
        } catch {
            dispatch(showError('error'))
        } finally {
            setDeleting(false)
        }
    }
    const handleCancelDelClick = () => setDel(false)

    const openAddDialog = () => {
        titleEditor?.commands.setContent('')
        setFormDescription('')
        setFormResponsible(defSimplePersonData)
        setFormDueDate(null)
        setEditItem('new')
    }

    const openEditDialog = (item: RiskAnalysisMitigationSchedule) => {
        titleEditor?.commands.setContent(item.title)
        setFormDescription(item.description)
        setFormResponsible({ userName: item.responsibleUserName, fullName: item.responsibleUserNameFullName })
        setFormDueDate(item.dueDate ? parseISODate(item.dueDate) : null)
        setEditItem(item)
    }

    const handleSave = async () => {
        setSaving(true)
        try {
            if (editItem === 'new') {
                const created = await dispatch(createRiskAnalysisMitigationSchedule({
                    documentId: Number(header.id),
                    reasonId: null,
                    parentId: null,
                    title: titleEditor?.getHTML() ?? '',
                    description: formDescription,
                    responsibleUserName: formResponsible.userName,
                    responsibleUserNameFullName: formResponsible.fullName,
                    dueDate: formDueDate ? formDueDate.toISOString() : null,
                })).unwrap()
                setItems(prev => [...prev, created])
            } else if (editItem) {
                const updated = await dispatch(updateRiskAnalysisMitigationSchedule({
                    id: editItem.id,
                    documentId: Number(header.id),
                    title: titleEditor?.getHTML() ?? '',
                    description: formDescription,
                    responsibleUserName: formResponsible.userName,
                    responsibleUserNameFullName: formResponsible.fullName,
                    dueDate: formDueDate ? formDueDate.toISOString() : null,
                })).unwrap()
                setItems(prev => prev.map(i => i.id === updated.id ? updated : i))
            }
            setEditItem(null)
        } catch {
            dispatch(showError('error'))
        } finally {
            setSaving(false)
        }
    }

    const handleGenerate = async () => {
        setGenerating(true)
        try {
            const result = await dispatch(generateRiskAnalysisMitigationSchedules({
                documentId: Number(header.id),
                clearExisting: false
            })).unwrap()
            setItems(result)
        } catch {
            dispatch(showError('error'))
        } finally {
            setGenerating(false)
        }
    }

    const handleConfirmRegenerate = async () => {
        setGenerating(true)
        try {
            const result = await dispatch(generateRiskAnalysisMitigationSchedules({
                documentId: Number(header.id),
                clearExisting,
            })).unwrap()
            setItems(result)
            setRegenerateOpen(false)
            setClearExisting(false)
        } catch {
            dispatch(showError('error'))
        } finally {
            setGenerating(false)
        }
    }

    const handleConfirmDeleteItem = async () => {
        if (!deleteItem) return
        setDeletingItem(true)
        try {
            await dispatch(deleteRiskAnalysisMitigationSchedule({ docId: header.id, id: deleteItem.id })).unwrap()
            setItems(prev => prev.filter(i => i.id !== deleteItem.id))
            setDeleteItem(null)
        } catch {
            dispatch(showError('error'))
        } finally {
            setDeletingItem(false)
        }
    }

    return <>
        <DocumentSectionHeader
            numeration={numeration}
            name={section.name}
            isEditable={isEditable}
            error={error}
            onSaveName={handleApprove}
            onDeleteClick={handleDeleteClick}
        />
        {del &&
            <DocumentSectionDeleteDialog
                deleting={deleting}
                hasSubSections={section.subSectionsIds.length > 0}
                onConfirm={handleConfirmDeleteClick}
                onCancel={handleCancelDelClick}
            />
        }
        <Divider />

        {loading
            ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>
            : items.length === 0
            ? <Box sx={{ mt: 1 }}>
                <LoadingButton
                    loading={generating}
                    variant="outlined"
                    onClick={handleGenerate}
                >
                    <Localized id="generate-schedule">Generate schedule</Localized>
                </LoadingButton>
              </Box>
            : <>
                <TableContainer component={Paper} sx={{ mt: 1 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 40 }}><Localized id="lp">No</Localized></TableCell>
                                <TableCell><Localized id="title">Title</Localized></TableCell>
                                <TableCell><Localized id="description">Description</Localized></TableCell>
                                <TableCell><Localized id="responsible">Responsible</Localized></TableCell>
                                <TableCell><Localized id="due-date">Due date</Localized></TableCell>
                                <TableCell sx={{
                                    width: 100,
                                }}><Localized id="generated-automatically">Generated automatically</Localized></TableCell>
                                {isEditable && <TableCell sx={{ width: 100 }} />}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item, index) => (
                                <TableRow key={item.id} hover>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell><div dangerouslySetInnerHTML={{ __html: item.title }} /></TableCell>
                                    <TableCell>{item.description}</TableCell>
                                    <TableCell>{item.responsibleUserNameFullName}</TableCell>
                                    <TableCell>
                                        {item.dueDate ? format(parseISO(item.dueDate), 'dd-MM-yyyy') : '—'}
                                    </TableCell>
                                    <TableCell sx={{
                                            textAlign: 'center'
                                        }}>
                                        {!item.isManual && <CheckCircleIcon fontSize="small" color="success" />}
                                    </TableCell>
                                    {isEditable &&
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            <IconButton size="small" onClick={() => openEditDialog(item)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => setDeleteItem(item)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    }
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {isEditable &&
                    <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                        <LoadingButton
                            startIcon={<AddIcon />}
                            onClick={openAddDialog}
                            size="small"
                            variant="outlined"
                        >
                            <Localized id="add">Add</Localized>
                        </LoadingButton>
                        <LoadingButton
                            loading={generating}
                            size="small"
                            variant="outlined"
                            onClick={() => setRegenerateOpen(true)}
                        >
                            <Localized id="regenerate-schedule">Regenerate schedule</Localized>
                        </LoadingButton>
                    </Box>
                }
            </>
        }

        {/* Add / Edit dialog */}
        <Dialog open={editItem !== null} onClose={() => setEditItem(null)} maxWidth="sm" fullWidth>
            <DialogTitle>
                {editItem === 'new'
                    ? <Localized id="add">Add</Localized>
                    : <Localized id="edit">Edit</Localized>
                }
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            <Localized id="title">Title</Localized>
                        </Typography>
                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1.5, py: 1, mt: 0.5 }}>
                            <JustText editor={titleEditor} />
                        </Box>
                    </Box>
                    <TextField
                        label={<Localized id="description">Description</Localized>}
                        value={formDescription}
                        onChange={e => setFormDescription(e.target.value)}
                        size="small"
                        fullWidth
                        multiline
                        rows={3}
                    />
                    <SimplePersonalDataPicker
                        data={formResponsible}
                        setData={setFormResponsible}
                        label={<Localized id="responsible">Responsible</Localized>}
                    />
                    <DatePicker
                        label={<Localized id="due-date">Due date</Localized>}
                        value={formDueDate}
                        onChange={val => setFormDueDate(val)}
                        slotProps={{
                            textField: {
                                size: 'small',
                                fullWidth: true,
                                InputLabelProps: { shrink: true },
                            },
                        }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <SaveButton loading={saving} onClick={handleSave} />
                <LoadingButton onClick={() => setEditItem(null)} disabled={saving}>
                    <Localized id="cancel">Cancel</Localized>
                </LoadingButton>
            </DialogActions>
        </Dialog>

        {/* Regenerate confirmation dialog */}
        <Dialog open={regenerateOpen} onClose={() => setRegenerateOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>
                <Localized id="regenerate-schedule">Regenerate schedule</Localized>
            </DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    <Localized id="regenerate-schedule-warning">
                        Regenerate will add new items based on the current risk analysis.
                    </Localized>
                </DialogContentText>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={clearExisting}
                            onChange={e => setClearExisting(e.target.checked)}
                        />
                    }
                    label={<Localized id="clear-existing">Clear existing items</Localized>}
                />
                {clearExisting &&
                    <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                        <Localized id="clear-existing-warning">
                            All existing items will be permanently removed.
                        </Localized>
                    </Typography>
                }
            </DialogContent>
            <DialogActions>
                <LoadingButton loading={generating} onClick={handleConfirmRegenerate} variant="contained">
                    <Localized id="regenerate-schedule">Regenerate</Localized>
                </LoadingButton>
                <LoadingButton onClick={() => setRegenerateOpen(false)} disabled={generating}>
                    <Localized id="cancel">Cancel</Localized>
                </LoadingButton>
            </DialogActions>
        </Dialog>

        {/* Delete item confirmation dialog */}
        <Dialog open={deleteItem !== null} onClose={() => setDeleteItem(null)}>
            <DialogContent>
                <DialogContentText>
                    <Localized id="confirm-delete">Do you want to delete?</Localized>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <LoadingButton loading={deletingItem} color="error" onClick={handleConfirmDeleteItem}>
                    <Localized id="delete">Delete</Localized>
                </LoadingButton>
                <LoadingButton onClick={() => setDeleteItem(null)} disabled={deletingItem}>
                    <Localized id="cancel">Cancel</Localized>
                </LoadingButton>
            </DialogActions>
        </Dialog>
    </>
}
